import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUser, getUserLoading } from '../../data/selectors/userAuthSelectors';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import ConfirmModal from '../confirmModal/ConfirmModal';
import {
  getStores,
  createStore,
  deleteStore,
  getDocuments,
  deleteDocument,
  uploadRecord,
  bulkUpload,
  bulkDelete,
  getJobs,
  getJob,
  cancelJob,
} from '../../services/geminiAdmin';
import styles from './geminiAdmin.module.css';

export default function GeminiAdmin() {
  const navigate = useNavigate();
  const user = useSelector(getUser);
  const userLoading = useSelector(getUserLoading);
  const [message, setMessage] = useState('');

  // Stores state
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [newStoreName, setNewStoreName] = useState('');

  // Documents state
  const [selectedStore, setSelectedStore] = useState('');
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // Sync operations state
  const [uploadUuid, setUploadUuid] = useState('');
  const [syncStore, setSyncStore] = useState('');
  const [deleteOrphans, setDeleteOrphans] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetailsLoading, setJobDetailsLoading] = useState(false);
  const pollingRef = useRef(null);

  // Modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null,
  });

  // Role protection
  useEffect(() => {
    if (user.role && user.role !== 'know-admin') {
      navigate('/');
    }
  }, [user.role, navigate]);

  // Page title
  useEffect(() => {
    document.title = 'KNOW - Gemini File Search Admin';
  }, []);

  // Auto-clear messages
  useEffect(() => {
    if (message === '') return;
    const timer = setTimeout(() => setMessage(''), 20000);
    return () => clearTimeout(timer);
  }, [message]);

  // Load stores on mount
  useEffect(() => {
    if (user.role === 'know-admin') {
      loadStores();
      loadJobs();
    }
  }, [user.role]);

  // Poll jobs when there are running jobs
  useEffect(() => {
    const hasRunningJobs = jobs.some(
      (job) => job.status === 'running' || job.status === 'cancelling'
    );
    if (hasRunningJobs && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        loadJobs();
      }, 3000);
    } else if (!hasRunningJobs && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [jobs]);

  const loadStores = useCallback(async () => {
    setStoresLoading(true);
    try {
      const { data } = await getStores();
      setStores(data.stores || []);
    } catch (err) {
      setMessage(err.message || 'Failed to load stores');
    } finally {
      setStoresLoading(false);
    }
  }, []);

  const loadDocuments = useCallback(async (storeName) => {
    if (!storeName) return;
    setDocumentsLoading(true);
    try {
      const { data } = await getDocuments(storeName);
      setDocuments(data.documents || []);
    } catch (err) {
      setMessage(err.message || 'Failed to load documents');
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const { data } = await getJobs();
      setJobs(data.jobs || []);
    } catch {
      // Silently fail on polling - don't show error for background updates
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const loadJobDetails = useCallback(async (jobId) => {
    setJobDetailsLoading(true);
    try {
      const { data } = await getJob(jobId);
      setSelectedJob(data.job);
    } catch (err) {
      setMessage(err.message || 'Failed to load job details');
    } finally {
      setJobDetailsLoading(false);
    }
  }, []);

  const closeJobDetails = () => {
    setSelectedJob(null);
  };

  const handleCreateStore = async (e) => {
    e.preventDefault();
    if (!newStoreName.trim()) return;
    setActionLoading(true);
    try {
      const { data } = await createStore(newStoreName.trim());
      setMessage(`Store created: ${data.store.displayName}`);
      setNewStoreName('');
      await loadStores();
    } catch (err) {
      setMessage(err.message || 'Failed to create store');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteStoreModal = (storeName) => {
    setModalConfig({
      isOpen: true,
      title: 'Delete Store',
      message: `Are you sure you want to delete store "${storeName}"? This will delete all documents in the store and cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await deleteStore(storeName);
          setMessage(`Store deleted: ${storeName}`);
          if (selectedStore === storeName) {
            setSelectedStore('');
            setDocuments([]);
          }
          if (syncStore === storeName) {
            setSyncStore('');
          }
          await loadStores();
        } catch (err) {
          setMessage(err.message || 'Failed to delete store');
        } finally {
          setActionLoading(false);
          closeModal();
        }
      },
    });
  };

  const openDeleteDocumentModal = (storeName, documentName) => {
    setModalConfig({
      isOpen: true,
      title: 'Delete Document',
      message: `Are you sure you want to delete document "${documentName}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setDocumentsLoading(true);
        try {
          await deleteDocument(storeName, documentName);
          setMessage(`Document deleted: ${documentName}`);
          await loadDocuments(storeName);
        } catch (err) {
          setMessage(err.message || 'Failed to delete document');
        } finally {
          setDocumentsLoading(false);
          closeModal();
        }
      },
    });
  };

  const handleUploadRecord = async (e) => {
    e.preventDefault();
    if (!syncStore || !uploadUuid.trim()) return;
    setActionLoading(true);
    try {
      const { data } = await uploadRecord(syncStore, uploadUuid.trim());
      setMessage(`Record uploaded: ${data.uuid}`);
      setUploadUuid('');
    } catch (err) {
      setMessage(err.message || 'Failed to upload record');
    } finally {
      setActionLoading(false);
    }
  };

  const openBulkUploadModal = () => {
    if (!syncStore) return;
    setModalConfig({
      isOpen: true,
      title: 'Bulk Upload',
      message: `Start bulk upload to store "${syncStore}"?${deleteOrphans ? ' This will also delete documents not present in the database.' : ''}`,
      confirmText: 'Start Upload',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const { data } = await bulkUpload(syncStore, deleteOrphans);
          setMessage(`Bulk upload started: ${data.jobId}`);
          await loadJobs();
        } catch (err) {
          setMessage(err.message || 'Failed to start bulk upload');
        } finally {
          setActionLoading(false);
          closeModal();
        }
      },
    });
  };

  const openBulkDeleteModal = () => {
    if (!syncStore) return;
    setModalConfig({
      isOpen: true,
      title: 'Clear All Documents',
      message: `Are you sure you want to delete ALL documents from store "${syncStore}"? This cannot be undone.`,
      confirmText: 'Delete All',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const { data } = await bulkDelete(syncStore);
          setMessage(`Bulk delete started: ${data.jobId}`);
          await loadJobs();
        } catch (err) {
          setMessage(err.message || 'Failed to start bulk delete');
        } finally {
          setActionLoading(false);
          closeModal();
        }
      },
    });
  };

  const openCancelModal = (jobId) => {
    setModalConfig({
      isOpen: true,
      title: 'Cancel Job',
      message: `Are you sure you want to cancel job "${jobId}"?`,
      confirmText: 'Cancel Job',
      cancelText: 'Keep Running',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const { data } = await cancelJob(jobId);
          setMessage(`Job cancellation requested: ${data.jobId} (${data.status})`);
          await loadJobs();
        } catch (err) {
          setMessage(err.message || 'Failed to cancel job');
        } finally {
          setActionLoading(false);
          closeModal();
        }
      },
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const handleStoreSelect = (storeName) => {
    setSelectedStore(storeName);
    loadDocuments(storeName);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const renderProgressBar = (progress) => {
    if (!progress) return null;
    const { percent, phase, processed, total } = progress;
    return (
      <div className={styles.progressContainer}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${percent}%` }} />
        </div>
        <span className={styles.progressText}>
          {phase}: {processed}/{total} ({percent}%)
        </span>
      </div>
    );
  };

  if (userLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.adminContainer}>
      <h1>Gemini File Search Administration</h1>

      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
        confirmCB={modalConfig.onConfirm}
        cancelCB={closeModal}
      />

      <p className={styles.message}>{message}</p>

      {/* Stores Management */}
      <details className={styles.functionSection} open>
        <summary>Stores Management</summary>
        <div className={styles.sectionContent}>
          <form onSubmit={handleCreateStore} className={styles.form}>
            <label htmlFor="storeName">New Store Name:</label>
            <input
              id="storeName"
              type="text"
              value={newStoreName}
              onChange={(e) => setNewStoreName(e.target.value)}
              placeholder="e.g., knowoncology-staging"
              disabled={actionLoading}
            />
            <button type="submit" disabled={actionLoading || !newStoreName.trim()}>
              {actionLoading ? 'Creating...' : 'Create Store'}
            </button>
          </form>

          {storesLoading ? (
            <LoadingSpinner />
          ) : (
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Display Name</th>
                  <th>Resource Name</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stores.map((store) => (
                  <tr key={store.name}>
                    <td>{store.displayName}</td>
                    <td className={styles.code}>{store.name}</td>
                    <td>{formatDate(store.createTime)}</td>
                    <td>{formatDate(store.updateTime)}</td>
                    <td>
                      <button
                        onClick={() => openDeleteStoreModal(store.name.split('/').pop())}
                        disabled={actionLoading}
                        className={styles.dangerButton}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {stores.length === 0 && (
                  <tr>
                    <td colSpan="5" className={styles.emptyState}>
                      No stores found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </details>

      {/* Store Documents */}
      <details className={styles.functionSection}>
        <summary>Store Documents</summary>
        <div className={styles.sectionContent}>
          <div className={styles.form}>
            <label htmlFor="storeSelect">Select Store:</label>
            <select
              id="storeSelect"
              value={selectedStore}
              onChange={(e) => handleStoreSelect(e.target.value)}
              disabled={storesLoading}
            >
              <option value="">-- Select a store --</option>
              {stores.map((store) => (
                <option key={store.name} value={store.name.split('/').pop()}>
                  {store.displayName}
                </option>
              ))}
            </select>
          </div>

          {selectedStore && (
            <>
              {documentsLoading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <p className={styles.count}>Documents: {documents.length}</p>
                  <table className={styles.dataTable}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Created</th>
                        <th>Metadata</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc) => {
                        const docName = doc.name.split('/').pop();
                        const uuid =
                          doc.customMetadata?.find((m) => m.key === 'uuid')?.stringValue || '-';
                        return (
                          <tr key={doc.name}>
                            <td>{doc.displayName || docName}</td>
                            <td>{formatDate(doc.createTime)}</td>
                            <td className={styles.code}>UUID: {uuid}</td>
                            <td>
                              <button
                                onClick={() => openDeleteDocumentModal(selectedStore, docName)}
                                disabled={documentsLoading}
                                className={styles.dangerButton}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {documents.length === 0 && (
                        <tr>
                          <td colSpan="4" className={styles.emptyState}>
                            No documents in this store
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </div>
      </details>

      {/* Sync Operations */}
      <details className={styles.functionSection}>
        <summary>Sync Operations</summary>
        <div className={styles.sectionContent}>
          <div className={styles.form}>
            <label htmlFor="syncStoreSelect">Select Store:</label>
            <select
              id="syncStoreSelect"
              value={syncStore}
              onChange={(e) => setSyncStore(e.target.value)}
              disabled={storesLoading}
            >
              <option value="">-- Select a store --</option>
              {stores.map((store) => (
                <option key={store.name} value={store.name.split('/').pop()}>
                  {store.displayName}
                </option>
              ))}
            </select>
          </div>

          <form onSubmit={handleUploadRecord} className={styles.form}>
            <label htmlFor="uploadUuid">Upload Single Record (UUID):</label>
            <input
              id="uploadUuid"
              type="text"
              value={uploadUuid}
              onChange={(e) => setUploadUuid(e.target.value)}
              placeholder="Enter record UUID"
              disabled={actionLoading || !syncStore}
            />
            <button type="submit" disabled={actionLoading || !syncStore || !uploadUuid.trim()}>
              {actionLoading ? 'Uploading...' : 'Upload Record'}
            </button>
          </form>

          <div className={styles.form}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={deleteOrphans}
                onChange={(e) => setDeleteOrphans(e.target.checked)}
                disabled={actionLoading}
              />
              Delete orphaned documents (full sync mode)
            </label>
            <button onClick={openBulkUploadModal} disabled={actionLoading || !syncStore}>
              {actionLoading ? 'Starting...' : 'Start Bulk Upload'}
            </button>
          </div>

          <div className={styles.form}>
            <button
              onClick={openBulkDeleteModal}
              disabled={actionLoading || !syncStore}
              className={styles.dangerButton}
            >
              {actionLoading ? 'Starting...' : 'Clear All Documents'}
            </button>
          </div>
        </div>
      </details>

      {/* Job Monitor */}
      <details className={styles.functionSection}>
        <summary>Job Monitor</summary>
        <div className={styles.sectionContent}>
          {jobsLoading && jobs.length === 0 ? (
            <LoadingSpinner />
          ) : (
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Store</th>
                  <th>Progress</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.jobId}>
                    <td className={styles.code}>{job.jobId}</td>
                    <td>{job.type}</td>
                    <td>
                      <span className={`${styles.status} ${styles[job.status]}`}>{job.status}</span>
                    </td>
                    <td>{job.store?.split('/').pop() || '-'}</td>
                    <td>
                      {job.status === 'running' && renderProgressBar(job.progress)}
                      {job.status === 'completed' && job.result && (
                        <span className={styles.result}>
                          {job.type === 'bulk-upload'
                            ? `Success: ${job.result.successful}, Failed: ${job.result.failed}`
                            : `Deleted: ${job.result.deleted || 0}`}
                        </span>
                      )}
                      {job.status === 'failed' && <span className={styles.error}>{job.error}</span>}
                    </td>
                    <td>{formatDate(job.startedAt)}</td>
                    <td>{formatDate(job.completedAt)}</td>
                    <td>
                      <button
                        className={styles.viewButton}
                        onClick={() => loadJobDetails(job.jobId)}
                        disabled={jobDetailsLoading}
                      >
                        View Details
                      </button>
                      {(job.status === 'running' || job.status === 'cancelling') && (
                        <button
                          className={styles.cancelButton}
                          onClick={() => openCancelModal(job.jobId)}
                          disabled={actionLoading || job.status === 'cancelling'}
                        >
                          {job.status === 'cancelling' ? 'Cancelling...' : 'Cancel'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {jobs.length === 0 && (
                  <tr>
                    <td colSpan="8" className={styles.emptyState}>
                      No jobs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </details>

      <p className={styles.message}>{message}</p>

      {/* Job Details Modal */}
      {selectedJob && (
        <div className={styles.modalOverlay} onClick={closeJobDetails}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Job Details: {selectedJob.jobId}</h2>
              <button className={styles.closeButton} onClick={closeJobDetails}>
                ×
              </button>
            </div>

            <div className={styles.jobDetails}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Job ID:</span>
                <span className={styles.code}>{selectedJob.jobId}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Type:</span>
                <span>{selectedJob.type}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Status:</span>
                <span className={`${styles.status} ${styles[selectedJob.status]}`}>
                  {selectedJob.status}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Store:</span>
                <span>{selectedJob.store?.split('/').pop() || '-'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Started:</span>
                <span>{formatDate(selectedJob.startedAt)}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Completed:</span>
                <span>{formatDate(selectedJob.completedAt)}</span>
              </div>

              {selectedJob.progress && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Progress:</span>
                  {renderProgressBar(selectedJob.progress)}
                </div>
              )}

              {selectedJob.result && (
                <div className={styles.detailSection}>
                  <h3>Result</h3>
                  <pre className={styles.codeBlock}>
                    {JSON.stringify(selectedJob.result, null, 2)}
                  </pre>
                </div>
              )}

              {selectedJob.error && (
                <div className={styles.detailSection}>
                  <h3>Error</h3>
                  <pre className={styles.errorBlock}>{selectedJob.error}</pre>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button onClick={closeJobDetails}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
